import React from "react";
import Slider from "@mui/material/Slider";
import { PropertySearchFilters } from "../../swr/properties";
import { formatAmount } from "../../utils/propertyUtils";
import {
  AMENITY_OPTIONS,
  PROPERTY_STATUS_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
} from "../../utils/propertySearchParams";
import styles from "./propertyGrid.module.scss";

const PRICE_MAX = 100000000;

interface FilterCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const FilterCheckbox: React.FC<FilterCheckboxProps> = ({ label, checked, onChange }) => (
  <label className="checkbox-item">
    {label}
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
    <span className="checkmark"></span>
  </label>
);

const parseAmount = (value: string) => {
  const digits = value.replace(/[^\d]/g, "");
  return digits === "" ? undefined : Number(digits);
};

export interface PropertyFiltersProps {
  filters: PropertySearchFilters;
  onChange: (filters: PropertySearchFilters) => void;
}

// The API filters by a single type and status, so those act like radio
// buttons; amenities combine (a property must have all of them).
const PropertyFilters: React.FC<PropertyFiltersProps> = ({ filters, onChange }) => {
  const [priceFrom, setPriceFrom] = React.useState(filters.priceFrom);
  const [priceTo, setPriceTo] = React.useState(filters.priceTo);

  // Keep the inputs in sync when the URL changes (e.g. "Clear all").
  React.useEffect(() => {
    setPriceFrom(filters.priceFrom);
    setPriceTo(filters.priceTo);
  }, [filters.priceFrom, filters.priceTo]);

  const update = (patch: Partial<PropertySearchFilters>) =>
    onChange({ ...filters, ...patch });

  const toggleAmenity = (amenity: string, checked: boolean) => {
    const current = filters.amenities ?? [];
    const next = checked
      ? [...current, amenity]
      : current.filter((a) => a !== amenity);
    update({ amenities: next.length ? next : undefined });
  };

  const applyPrice = () => {
    const [from, to] =
      priceFrom !== undefined && priceTo !== undefined && priceFrom > priceTo
        ? [priceTo, priceFrom]
        : [priceFrom, priceTo];
    update({ priceFrom: from, priceTo: to });
  };

  const hasFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <div className="widget ltn__menu-widget">
      {hasFilters && (
        <button
          type="button"
          className={`btn btn-transparent ${styles.clearFilters}`}
          onClick={() => onChange({})}
        >
          Clear all filters
        </button>
      )}

      <h4 className="ltn__widget-title">Property Type</h4>
      <ul>
        {PROPERTY_TYPE_OPTIONS.map((option) => (
          <li key={option.value}>
            <FilterCheckbox
              label={option.label}
              checked={filters.type === option.value}
              onChange={(checked) =>
                update({ type: checked ? option.value : undefined })
              }
            />
          </li>
        ))}
      </ul>
      <hr />

      <h4 className="ltn__widget-title">Category</h4>
      <ul>
        {PROPERTY_STATUS_OPTIONS.map((option) => (
          <li key={option.value}>
            <FilterCheckbox
              label={option.label}
              checked={filters.status === option.value}
              onChange={(checked) =>
                update({ status: checked ? option.value : undefined })
              }
            />
          </li>
        ))}
      </ul>
      <hr />

      <h4 className="ltn__widget-title">Amenities</h4>
      <ul>
        {AMENITY_OPTIONS.map((option) => (
          <li key={option.value}>
            <FilterCheckbox
              label={option.label}
              checked={!!filters.amenities?.includes(option.value)}
              onChange={(checked) => toggleAmenity(option.value, checked)}
            />
          </li>
        ))}
      </ul>
      <hr />

      <div className="ltn__price-filter-widget">
        <h4 className="ltn__widget-title">Filter by price</h4>
        <div className="price_filter">
          <div className={styles.priceInputs}>
            <label>
              <span>₦</span>
              <input
                type="text"
                inputMode="numeric"
                className="amount"
                placeholder="Min"
                value={priceFrom !== undefined ? formatAmount(String(priceFrom)) : ""}
                onChange={(e) => setPriceFrom(parseAmount(e.target.value))}
                autoComplete="off"
              />
            </label>
            <label>
              <span>₦</span>
              <input
                type="text"
                inputMode="numeric"
                className="amount"
                placeholder="Max"
                value={priceTo !== undefined ? formatAmount(String(priceTo)) : ""}
                onChange={(e) => setPriceTo(parseAmount(e.target.value))}
                autoComplete="off"
              />
            </label>
          </div>
          <Slider
            getAriaLabel={() => "Price range"}
            value={[priceFrom ?? 0, priceTo ?? PRICE_MAX]}
            onChange={(_, value) => {
              const [from, to] = value as number[];
              setPriceFrom(from > 0 ? from : undefined);
              setPriceTo(to < PRICE_MAX ? to : undefined);
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `₦${formatAmount(String(v))}`}
            min={0}
            max={PRICE_MAX}
            step={50000}
            sx={{ color: "var(--ltn__secondary-color)" }}
          />
          <button
            type="button"
            className="btn theme-btn-1 btn-effect-1"
            onClick={applyPrice}
          >
            Apply price
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyFilters;
